import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Leaf, Plus, RefreshCw, AlertTriangle, Trash2,
  Calendar, Package, ExternalLink
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { carbonCreditsApi, CarbonCredit } from '../api/partner.api';

const CREDIT_TYPES = ['VCS','Gold_Standard','CDM','ACR','CAR','Plan_Vivo','other'];

function daysUntil(dateStr: string): number {
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function retirementBadge(credit: CarbonCredit) {
  if (credit.status === 'retired') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">Retired</span>;
  if (credit.status === 'cancelled') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">Cancelled</span>;
  const days = daysUntil(credit.retirement_date);
  if (days < 0)  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Expired</span>;
  if (days <= 30) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 animate-pulse">Expires in {days}d</span>;
  if (days <= 60) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Expires in {days}d</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Active · {days}d left</span>;
}

interface FormState {
  name: string; provider: string; credit_type: string;
  volume_tonnes: string; purchase_date: string;
  cost_per_tonne: string; total_cost: string; currency: string;
  certificate_url: string; serial_number: string; notes: string;
}

const EMPTY: FormState = {
  name: '', provider: '', credit_type: 'other', volume_tonnes: '',
  purchase_date: '', cost_per_tonne: '', total_cost: '', currency: 'GBP',
  certificate_url: '', serial_number: '', notes: ''
};

export default function CarbonCredits({ companyIdProp }: { companyIdProp?: number }) {
  const params = useParams<{ companyId?: string; id?: string }>();
  const { user } = useAuth();
  const companyId = companyIdProp ?? (parseInt(params.companyId ?? params.id ?? '0') || user?.company_id);

  const [credits, setCredits] = useState<CarbonCredit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (companyId) load(); }, [companyId]);

  async function load() {
    setLoading(true);
    try {
      const res = await carbonCreditsApi.list(companyId);
      setCredits(res.credits);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name || !form.volume_tonnes || !form.purchase_date) {
      setError('Name, volume and purchase date are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await carbonCreditsApi.create(companyId, {
        name: form.name,
        provider: form.provider || undefined,
        credit_type: form.credit_type,
        volume_tonnes: parseFloat(form.volume_tonnes),
        purchase_date: form.purchase_date,
        cost_per_tonne: form.cost_per_tonne ? parseFloat(form.cost_per_tonne) : undefined,
        total_cost: form.total_cost ? parseFloat(form.total_cost) : undefined,
        currency: form.currency,
        certificate_url: form.certificate_url || undefined,
        serial_number: form.serial_number || undefined,
        notes: form.notes || undefined
      } as any);
      setForm({ ...EMPTY });
      setShowForm(false);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm('Delete this carbon credit?')) return;
    await carbonCreditsApi.delete(companyId, id);
    load();
  }

  const totalActive = credits.filter(c => c.status === 'active').reduce((s, c) => s + Number(c.volume_tonnes), 0);
  const expiringSoon = credits.filter(c => c.status === 'active' && daysUntil(c.retirement_date) <= 60).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-emerald-100 rounded-xl">
            <Leaf className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Carbon Credits</h2>
            <p className="text-xs text-slate-500">
              {(totalActive || 0).toFixed(1)}t CO₂e active offset
              {expiringSoon > 0 && <span className="ml-2 text-amber-600 font-medium">· {expiringSoon} expiring within 60 days</span>}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Credit
        </button>
      </div>

      {/* Add form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-3xl border border-slate-100 p-5 space-y-4">
          <h3 className="text-sm font-semibold text-slate-900">New Carbon Credit</h3>
          {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">Credit Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="e.g. Kenyan Reforestation Project 2025" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Provider</label>
              <input type="text" value={form.provider} onChange={e => setForm(f => ({...f, provider: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="e.g. Gold Standard" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Credit Type</label>
              <select value={form.credit_type} onChange={e => setForm(f => ({...f, credit_type: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                {CREDIT_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Volume (tonnes CO₂e) *</label>
              <input type="number" step="0.01" value={form.volume_tonnes} onChange={e => setForm(f => ({...f, volume_tonnes: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="100.00" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Purchase Date *</label>
              <input type="date" value={form.purchase_date} onChange={e => setForm(f => ({...f, purchase_date: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              {form.purchase_date && (
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Retires: {new Date(new Date(form.purchase_date).setFullYear(new Date(form.purchase_date).getFullYear() + 1)).toLocaleDateString()}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Cost per Tonne ({form.currency})</label>
              <input type="number" step="0.01" value={form.cost_per_tonne} onChange={e => setForm(f => ({...f, cost_per_tonne: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="15.00" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Serial Number</label>
              <input type="text" value={form.serial_number} onChange={e => setForm(f => ({...f, serial_number: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Registry serial number" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Certificate URL</label>
              <input type="url" value={form.certificate_url} onChange={e => setForm(f => ({...f, certificate_url: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="https://..." />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setShowForm(false); setError(null); }}
              className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60">
              {saving ? 'Saving...' : 'Add Credit'}
            </button>
          </div>
        </form>
      )}

      {/* Credits list */}
      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : credits.length === 0 ? (
        <div className="py-12 bg-white rounded-3xl border border-slate-100">
          <Leaf className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-400">No carbon credits recorded yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Volume</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Purchase Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Retirement</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {credits.map(c => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-left">
                    <div className="font-medium text-slate-900">{c.name}</div>
                    {c.provider && <div className="text-xs text-slate-400">{c.provider}</div>}
                  </td>
                  <td className="px-4 py-3 text-left text-slate-500 text-xs">{(c.credit_type || 'other').replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-left font-medium text-slate-900">{Number(c.volume_tonnes || 0).toFixed(1)}t</td>
                  <td className="px-4 py-3 text-left text-slate-500">{c.purchase_date ? new Date(c.purchase_date).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3 text-left text-slate-500">{c.retirement_date ? new Date(c.retirement_date).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3 text-left">{retirementBadge(c)}</td>
                  <td className="px-4 py-3 text-left">
                    <div className="flex items-center gap-2 justify-end">
                      {c.certificate_url && (
                        <a href={c.certificate_url} target="_blank" rel="noopener noreferrer"
                          className="p-1 text-slate-400 hover:text-blue-600 rounded" title="View certificate">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <button onClick={() => handleDelete(c.id)}
                        className="p-1 text-slate-300 hover:text-red-500 rounded transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
